import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { ADSENSE_CONFIG } from './adConfig';

interface AdSenseContextType {
  isLoaded: boolean;
  isEnabled: boolean;
  refreshAd: (slotId: string) => void;
}

const AdSenseContext = createContext<AdSenseContextType>({
  isLoaded: false,
  isEnabled: false,
  refreshAd: () => {},
});

export const useAdSense = () => useContext(AdSenseContext);

interface AdSenseProviderProps {
  children: React.ReactNode;
}

/**
 * AdSenseProvider
 * 
 * Wraps your app and handles loading the Google AdSense script.
 * The script is loaded once in the document head.
 * 
 * Usage:
 * <AdSenseProvider>
 *   <App />
 * </AdSenseProvider>
 */
export const AdSenseProvider: React.FC<AdSenseProviderProps> = ({ children }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const { enabled, publisherId } = ADSENSE_CONFIG;

  useEffect(() => {
    // Don't load if ads are disabled
    if (!enabled) {
      console.log('[AdSense] Ads disabled via config');
      return;
    }

    // Check if script is already loaded
    const existingScript = document.querySelector(
      `script[src*="pagead2.googlesyndication.com"]`
    );
    
    if (existingScript) {
      console.log('[AdSense] Script already loaded');
      setIsLoaded(true);
      return;
    }

    // Create and load the AdSense script
    const script = document.createElement('script');
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${publisherId}`;
    script.async = true;
    script.crossOrigin = 'anonymous';
    
    script.onload = () => {
      console.log('[AdSense] Script loaded successfully');
      setIsLoaded(true);
    };
    
    script.onerror = () => {
      console.error('[AdSense] Failed to load script');
    };

    // Add to document head
    document.head.appendChild(script);

    return () => {
      // Cleanup is not needed for AdSense script
      // Removing it could break ads already rendered
    };
  }, [enabled, publisherId]);

  // Function to refresh a specific ad slot (useful for SPA navigation)
  const refreshAd = useCallback((slotId: string) => {
    if (!isLoaded || !enabled) return;
    
    try {
      // AdSense auto-refreshes on visibility changes, but this can force it
      const adsbygoogle = (window as any).adsbygoogle;
      if (adsbygoogle) {
        adsbygoogle.push({});
      }
    } catch (error) {
      console.error('[AdSense] Error refreshing ad:', error);
    }
  }, [isLoaded, enabled]);

  return (
    <AdSenseContext.Provider value={{ isLoaded, isEnabled: enabled, refreshAd }}>
      {children}
    </AdSenseContext.Provider>
  );
};

export default AdSenseProvider;
